/* Field Intake & Job Creation — pure helpers (no React), unit-tested in
   fieldIntakeLogic.test.ts: the due time, and readers over the field app's
   record / issued eCOC documents (shapes: Field app lib/data/persist.dart and
   lib/state/ecoc_document.dart). */
import type { FieldIntakePhoto, Json } from './types';

const pad = (n: number) => String(n).padStart(2, '0');

/** The audit form name (dbo.AuditData.FormName) — also in aiAuditForms.ts. */
export const FIELD_INTAKE_FORM = 'Field Intake';

/** Service category code (AI_ServiceTask.CategoryCode) → its name (ServiceDefinitions.cs). */
export const categoryName = (code: string | null): string | null =>
  ({ SUR: 'Survey', SAM: 'Sampling', PIC: 'Pickup', DEL: 'Delivery', BOT: 'Bottle Order' } as Record<string, string>)[code ?? ''] ?? code;

/** Intake status → the shared pill variant (backlog.css .bl-pill). */
export function intakePill(s: string): string {
  return s === 'Ready' ? 'ok' : s === 'Needs review' ? 'warn' : s === 'Blocked' ? 'alert' : 'info';
}

/** A server error's message for the user (the API's {error:{message}} envelope). */
export function errorText(e: unknown, fallback: string): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
  return r ?? (e instanceof Error ? e.message : fallback);
}

/** ISO local `yyyy-mm-ddTHH:mm` for a Date (what fmtLocalDateTime reads). */
export function isoLocal(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `MM/DD/YYYY` (the eCOC's date) → Date at midnight, or null. */
export function parseUsDate(text: string | null | undefined): Date | null {
  const m = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(text ?? '');
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  return d.getMonth() === Number(m[1]) - 1 ? d : null;
}

export interface Due { overdue: boolean; today: boolean; }

/**
 * Overdue / due today for a due date the server worked out (the Job's own rule:
 * received time plus the turnaround in working days — FieldIntakeRules.ExpectedDue).
 * The wire value is the stored wall-clock (`yyyy-mm-ddTHH:mm…`, ADR-0008), read as such.
 */
export function dueState(value: string | null | undefined, now = new Date()): Due | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(value ?? '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0));
  return { overdue: d < now, today: d.toDateString() === now.toDateString() };
}

/** Time on site, from arrival to completion ("1 h 25 min"); '' when either is missing or out of order.
 *  Both are stored wall-clock values (`yyyy-mm-ddTHH:mm…`, ADR-0008), read as such. */
export function onSiteDuration(from: string | null | undefined, to: string | null | undefined): string {
  const at = (v: string | null | undefined) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v ?? '');
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5])).getTime() : null;
  };
  const a = at(from), b = at(to);
  if (a == null || b == null || b < a) return '';
  const min = Math.round((b - a) / 60000);
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h} h${m ? ` ${m} min` : ''}` : `${m} min`;
}

/** The arrival GPS the field app recorded in its audit ("On site at 16:41 — 13.0500, 80.2200 captured"). */
export function arrivalGps(record: Json | null): string {
  for (const a of arr(record?.audit).map(obj)) {
    if (str(a.action) !== 'Arrived on site') continue;
    const m = /—\s*(-?\d+(?:\.\d+)?,\s*-?\d+(?:\.\d+)?)\s*captured/.exec(str(a.detail));
    if (m?.[1]) return m[1];
  }
  return '';
}

/** "Validation (1 level)" from the record's review rungs (wf.rungs). */
export function reviewLevels(record: Json | null): string {
  const rungs = strs(obj(record?.wf).rungs);
  if (!rungs.length) return '';
  const names = rungs.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(' › ');
  return `${names} (${rungs.length} level${rungs.length === 1 ? '' : 's'})`;
}

/** Today minus `days`, as `yyyy-mm-dd` (the list's Date Range); null = All. */
export function fromDate(days: number | null, now = new Date()): string | null {
  if (days == null) return null;
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ── Loose JSON readers ─────────────────────────────────────────────────────
export const obj = (v: unknown): Json => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : {});
export const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
export const str = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
export const strs = (v: unknown): string[] => arr(v).map(str).filter(Boolean);

/** The inspection sheet's groups, in the app's order (Field app reference_data.dart). */
export const SITE_GROUPS: { key: string; label: string; required: boolean; options: string[] }[] = [
  { key: 'propertyType', label: 'Property Type', required: true, options: ['Residential', 'Commercial', 'Industrial', 'Multi-Family'] },
  { key: 'dwelling', label: 'Dwelling Type', required: true,
    options: ['House', 'Condominium', 'Apartment', 'Manufactured Home', 'Manufactured Office/Commercial', 'Office Building', 'Industrial Manufacturing', 'Medical Facility'] },
  { key: 'locations', label: 'Sample Locations', required: true,
    options: ['Kitchen', 'Bathroom', 'Master Bathroom', 'Bedroom 1', 'Bedroom 2', 'Bedroom 3', 'Living Room', 'Dining Room', 'Family Room', 'Mud Room',
              'Back Office', 'Back Common Areas', 'Basement', 'Basement Bathroom', 'Basement Office', 'Laundry Room', 'Garage', 'Office'] },
  { key: 'heating', label: 'Heating', required: false, options: ['Furnace', 'Heat Pump', 'Electric', 'Gas'] },
  { key: 'cooling', label: 'Cooling', required: false, options: ['Central Air', 'Mini Split', 'None'] },
  { key: 'waterHeater', label: 'Water Heater', required: false, options: ['Heat Pump', 'Tank Water Heater', 'Tankless'] },
  { key: 'roofing', label: 'Roofing', required: false, options: ['Slate', 'Shake', 'Shingle', 'Composite', 'Metal'] },
  { key: 'foundation', label: 'Foundation', required: false, options: ['Concrete Slab', 'Full Basement', 'Crawlspace', 'Block'] },
  { key: 'exterior', label: 'Exterior', required: false, options: ['Wood', 'Vinyl', 'Fiber Cement', 'Stucco', 'Brick', 'Stone'] },
  { key: 'present', label: 'People Present at Time of Inspection', required: true,
    options: ['Contractor', 'Building Owner', 'Listing Agent', 'Buyers Agent', 'Facilities Person', 'Customers', 'Tenant', 'None'] },
];

/** The standard exterior elevations the field app asks for. */
export const ELEVATIONS = ['Front elevation', 'Rear elevation', 'Left elevation', 'Right elevation', 'Street view'];

/** The tests the field app offers on its Chain of Custody (Field app lib/data/reference_data.dart
 *  kTests): the id the record keeps and the name the eCOC writes. */
export const APP_TESTS: { id: string; label: string }[] = [
  { id: 'PLM', label: 'PLM Bulk' }, { id: 'PC', label: 'Point Count (400)' },
  { id: 'XRF', label: 'X-ray Fluorescence' }, { id: 'PB', label: 'Paint Chip (Flame AA)' },
  { id: 'OTH', label: 'Other' },
];

/** The COC's chosen tests as the eCOC names them: the eCOC's own list, else the record's ids. */
export function chosenTests(ecocTests: string[], recordIds: string[]): string[] {
  if (ecocTests.length) return ecocTests;
  return recordIds.map(id => APP_TESTS.find(t => t.id === id)?.label ?? id);
}

/** What each declaration's signature covers (the field app's signing payload per kind). */
export const SIG_COVERS: Record<string, (samples: number) => string> = {
  coc: n => `tests, turnaround, collection date, relinquished by, ${n} sample${n === 1 ? '' : 's'}`,
  survey: () => 'site description, building information, samples',
  sampling: () => 'samples and their photos',
};

export const BUILDING: { key: string; label: string }[] = [
  { key: 'built', label: 'Built' }, { key: 'sqft', label: '# Sq Ft' }, { key: 'floors', label: 'Floors' },
  { key: 'bd', label: '# bd' }, { key: 'ba', label: '# ba' },
];

export interface SiteRow { label: string; required: boolean; values: string[]; other: string; options: string[]; }

/** Site description: the eCOC's (label-keyed) answers, else the record's; plus each group's "Other" text. */
export function siteRows(record: Json | null, ecoc: Json | null): SiteRow[] {
  const fromEcoc = obj(ecoc?.siteDescription);
  const survey = obj(record?.survey);
  const sel = obj(survey.sel);
  const other = obj(survey.other);
  return SITE_GROUPS.map(g => ({
    label: g.label,
    required: g.required,
    values: strs(fromEcoc[g.label]).length ? strs(fromEcoc[g.label]) : strs(sel[g.key]),
    other: str(other[g.key]),
    options: g.options,
  }));
}

/** Rooms: the ticked Sample Locations, then rooms the technician added on site. */
export function roomsOf(record: Json | null, ecoc: Json | null): { name: string; added: boolean }[] {
  const ticked = siteRows(record, ecoc).find(r => r.label === 'Sample Locations')?.values ?? [];
  const extra = strs(record?.extraStations).filter(x => !ticked.includes(x));
  return [...ticked.map(name => ({ name, added: false })), ...extra.map(name => ({ name, added: true }))];
}

export interface SampleLine {
  key: string;
  /** First row of a sample carries its own columns; later rows are its extra materials. */
  first: boolean;
  sampleNo: number;
  sampleId: string;
  format: 'Single' | 'Set';
  ha: string; location: string; condition: string; qty: string; collectedAt: string;
  photoCount: number;
  material: string; materialType: string; color: string; texture: string; friable: string; amount: string;
  readsAs: string;
  rowNo: number; rows: number;
  missing: string[];
}

/** One line per material row, in the eCOC's sample order (= SampleNo 1..n on the Job). */
export function sampleLines(record: Json | null, ecoc: Json | null): SampleLine[] {
  const recSamples = arr(record?.samples).map(obj);
  const source = ecoc ? arr(ecoc.samples).map(obj) : recSamples;
  const out: SampleLine[] = [];
  source.forEach((s, i) => {
    const id = str(ecoc ? s.sampleId : s.id) || String(i + 1);
    const rec = recSamples.find(r => str(r.id) === id) ?? {};
    const recRows = arr(rec.rows).map(obj);
    const mats = (ecoc ? arr(s.materials) : arr(s.rows)).map(obj);
    const rows = mats.length ? mats : [{}];
    const location = str(ecoc ? s.location : s.loc);
    const first = obj(rows[0]);
    const matl0 = str(ecoc ? first.materialSampled : first.matl);
    const friab0 = str(ecoc ? first.friableOrNonFriable : first.friab);
    const missing: string[] = [];
    if (!location) missing.push('location');
    if (!matl0) missing.push('material');
    else if (!friab0) missing.push('friable / non-friable');
    rows.forEach((m, k) => {
      const rr = obj(recRows[k]);
      out.push({
        key: `${id}#${k}`,
        first: k === 0,
        sampleNo: i + 1,
        sampleId: id,
        format: /[A-Za-z]$/.test(id) ? 'Set' : 'Single',
        ha: str(ecoc ? s.homogeneousArea : s.ha) || str(rec.ha),
        location,
        condition: str(ecoc ? s.materialCondition : s.cond),
        qty: str(ecoc ? s.approxSqLnFt : s.qty),
        collectedAt: str(ecoc ? s.collectedAt : s.ts),
        photoCount: arr(ecoc ? s.photographs : s.photos).length,
        material: str(ecoc ? m.materialSampled : m.matl),
        materialType: str(m.mtype) || str(rr.mtype),
        color: str(ecoc ? m.materialColor : m.color),
        texture: str(ecoc ? m.materialTexturePattern : m.texture),
        friable: str(ecoc ? m.friableOrNonFriable : m.friab),
        amount: str(m.amount) || str(rr.amount),
        readsAs: str(m.labDescription),
        rowNo: k + 1,
        rows: rows.length,
        missing,
      });
    });
  });
  return out;
}

/** Photo kinds as the field app stores them → the grouping the page shows. */
export function photoGroup(p: FieldIntakePhoto): 'Exterior' | 'Rooms' | 'Samples' {
  const k = (p.kind ?? '').toLowerCase();
  if (p.sampleId || k === 'sample') return 'Samples';
  if (k === 'room') return 'Rooms';
  return 'Exterior';
}

/** The field app's audit trail (record.audit) merged with the LIMS rows, newest first. */
export interface AuditLine { when: string; action: string; by: string; detail: string; source: 'Field app' | 'LIMS'; sort: number; }

export function auditLines(record: Json | null, lims: { createdDate: string | null; propertyName: string | null; oldValue: string | null; newValue: string | null; byName: string | null; formName: string | null }[]): AuditLine[] {
  const app: AuditLine[] = arr(record?.audit).map(obj).map(a => {
    const when = str(a.time);
    const t = Date.parse(when);
    return {
      when, action: str(a.action), by: str(a.by),
      detail: [str(a.on), str(a.detail)].filter(Boolean).join(' · '),
      source: 'Field app', sort: Number.isNaN(t) ? 0 : t,
    };
  });
  const db: AuditLine[] = lims.map(r => ({
    when: r.createdDate ?? '', action: r.propertyName ?? r.formName ?? '', by: r.byName ?? '',
    detail: r.oldValue ? `${r.oldValue} → ${r.newValue ?? ''}` : (r.newValue ?? ''),
    source: 'LIMS', sort: r.createdDate ? Date.parse(r.createdDate) : 0,
  }));
  return [...app, ...db].sort((a, b) => b.sort - a.sort);
}
